import * as jre from "json-rules-engine"

export const questionType = {
    oneAnswer: "oneAnswer",
    anyThatApply: "anyThatApply",
} as const

export type QuestionType = (typeof questionType)[keyof typeof questionType]

export type Markdown = string & { __brand: "Markdown" }

export type Condition = jre.TopLevelCondition

// `Identifier` is locally unique within the schema.
// If a question has id "123", that is unavailable for subquestions or results.
export type Identifier = string & { __brand: "Id" }

/**
 * Metadata attached to a question, subquestion, or result.
 *
 * Each property serves a distinct semantic purpose and may be rendered differently
 * depending on context
 */
export interface Info {
    /**
     * A full description of the item.
     *
     * - For questions: typically the main question text.
     * - For results:
     *   - If `detail` is present, this should serve as the **ingress** — a short lead-in summarising the result.
     *   - If `detail` is absent, this represents the full content of the result.
     */
    readonly description?: string

    /**
     * Detailed content in Markdown format. This is used for rich explanations,
     * examples, or embedded lists and can appear in an expandable/collapsible area.
     */
    readonly detail?: Markdown

    /**
     * A secondary aid to help users understand or answer the question.
     * Might be rendered inline or behind a “Show hint”.
     */
    readonly hint?: string

    /**
     * A very brief tooltip-style helper
     * Typically just a few words or a short sentence.
     */
    readonly tooltip?: string

    /**
     * Background context that helps explain *why* the question or result exists.
     * For example, legal background, conceptual framing, or historical note.
     */
    readonly context?: string

    /**
     * A textual reference to a larger or canonical source.
     * For example, "See Article 6 of the EU AI Act".
     * Does not imply a clickable link (though a link property could be added later).
     */
    readonly reference?: string

    /**
     * A classification or grouping label that helps organise items into broader themes.
     * Can be used for filtering, navigation, or visual organisation in the UI.
     * "Technical", "Legal", "Security", "Compliance"...
     */
    readonly category?: string
}

export interface InfoBearer {
    /**
     * A short, human-readable label or heading.
     *
     * - For questions: since questions are also section headings, this is the heading; the `description` holds the full question text.
     * - For subquestions: typically phrased as the question itself.
     * - For results: serves as the headline or name of the result.
     *
     * Intended to be concise and suitable as a UI heading.
     */
    readonly title: string

    /**
     * Semantic metadata for rendering.
     */
    readonly info: Info
}

export interface CommonQuestion extends InfoBearer {
    readonly type: QuestionType
    readonly id: Identifier
    readonly options: readonly Choice[]
}

export interface Question extends CommonQuestion {
    readonly subquestions: readonly Subquestion[]
}

export interface Subquestion extends CommonQuestion {
    readonly when: Condition
}

export interface Choice extends InfoBearer {
    readonly id: Identifier
}

export interface Result extends InfoBearer {
    readonly id: Identifier
    readonly when: Condition
}

export interface Schema {
    readonly questions: readonly Question[]
    readonly results: readonly Result[]
}

export type Fact = string | string[]

export type Facts = Readonly<Record<string, Fact | undefined>>
export type EngineFacts = Readonly<Record<string, any>>

export type MetaFacts = Readonly<{
    $nextQuestion: string | null
    $activeSubQuestions: ReadonlyArray<string>
}>
